import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { notesApi } from '@/services/notes';
import { TextNoteRequest } from '@/types';
import toast from 'react-hot-toast';

interface TextNotesProps {
  meetingId: number;
  meetingDate: string;
  meetingTime: string;
  location: string;
  onComplete?: (noteId: number) => void;
}

export const TextNotes = ({
  meetingId,
  onComplete,
}: TextNotesProps) => {
  const [text, setText] = useState('');
  const [processedText, setProcessedText] = useState<string>('');
  const [editableProcessedText, setEditableProcessedText] = useState<string>('');
  const [useAI, setUseAI] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<'original' | 'processed'>('processed');
  const [isSaved, setIsSaved] = useState(false);

  // Auto-save to localStorage every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (text.trim()) {
        localStorage.setItem(`meeting-${meetingId}-text-notes`, text);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [text, meetingId]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(`meeting-${meetingId}-text-notes`);
    if (saved) {
      setText(saved);
    }
  }, [meetingId]);

  const handleProcessWithAI = async () => {
    if (!text.trim()) {
      toast.error('Wprowadź tekst notatki');
      return;
    }

    setIsProcessing(true);

    try {
      const response = await notesApi.processTextWithAI({ content: text });

      if (response.success) {
        setProcessedText(response.processedContent);
        setEditableProcessedText(response.processedContent);
        setIsProcessed(true);
        toast.success('Tekst przetworzony przez AI!');
      }
    } catch (error) {
      toast.error('Błąd podczas przetwarzania przez AI');
      console.error('AI processing error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!text.trim()) {
      toast.error('Wprowadź tekst notatki');
      return;
    }

    setIsProcessing(true);

    try {
      const noteData: TextNoteRequest = {
        meetingId,
        rawContent: text,
        processedContent: selectedVersion === 'processed' ? editableProcessedText : undefined,
        useAI: useAI && isProcessed,
      };

      const response = await notesApi.uploadTextNote(noteData);

      if (response.success) {
        setIsSaved(true);
        localStorage.removeItem(`meeting-${meetingId}-text-notes`);
        toast.success('Notatka zapisana!');

        if (onComplete) {
          onComplete(response.note.id);
        }
      }
    } catch (error) {
      toast.error('Błąd podczas zapisywania notatki');
      console.error('Text note error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Czy na pewno chcesz wyczyścić tekst?')) {
      setText('');
      setProcessedText('');
      setEditableProcessedText('');
      setIsProcessed(false);
      setSelectedVersion('processed');
      setIsSaved(false);
      localStorage.removeItem(`meeting-${meetingId}-text-notes`);
    }
  };

  const handleNewNote = () => {
    setText('');
    setProcessedText('');
    setEditableProcessedText('');
    setIsProcessed(false);
    setSelectedVersion('processed');
    setIsSaved(false);
    setUseAI(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-foreground">Notatki tekstowe</h3>
        <div className="text-sm text-muted-foreground">
          {text.length} znaków
        </div>
      </div>

      {!isSaved ? (
        <>
          {/* Step 1: Input text + AI toggle */}
          {!isProcessed && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  Twoje notatki
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Wpisz swoje notatki ze spotkania..."
                  className="w-full h-64 px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  disabled={isProcessing}
                />
                <p className="text-xs text-muted-foreground">
                  Automatyczny zapis co 10 sekund
                </p>
              </div>

              {/* AI Toggle */}
              <div className="flex items-center gap-3 p-3 bg-card border border-border rounded-md">
                <input
                  type="checkbox"
                  id="use-ai"
                  checked={useAI}
                  onChange={(e) => setUseAI(e.target.checked)}
                  className="w-4 h-4 cursor-pointer"
                  disabled={isProcessing}
                />
                <label htmlFor="use-ai" className="text-sm font-medium text-foreground cursor-pointer">
                  Użyj AI do poprawy i formatowania tekstu
                </label>
              </div>

              <div className="flex gap-2">
                {useAI ? (
                  <Button onClick={handleProcessWithAI} disabled={isProcessing || !text.trim()}>
                    {isProcessing ? 'Przetwarzanie przez AI...' : 'Przetwórz przez AI'}
                  </Button>
                ) : (
                  <Button onClick={handleSave} disabled={isProcessing || !text.trim()}>
                    {isProcessing ? 'Zapisywanie...' : 'Zapisz'}
                  </Button>
                )}
                <Button onClick={handleClear} variant="outline" disabled={isProcessing}>
                  Wyczyść
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Comparison + Edit + Save */}
          {isProcessed && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Original text */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-foreground">Oryginał</h4>
                  <div className="p-4 bg-card border border-border rounded-md h-64 overflow-y-auto">
                    <p className="text-sm text-foreground whitespace-pre-wrap">{text}</p>
                  </div>
                </div>

                {/* Processed text (editable) */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                    Przetworzone przez AI
                    <span className="text-xs text-accent">✓ edytowalne</span>
                  </h4>
                  <textarea
                    value={editableProcessedText}
                    onChange={(e) => setEditableProcessedText(e.target.value)}
                    className="w-full h-64 px-3 py-2 bg-input border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none whitespace-pre-wrap"
                  />
                </div>
              </div>

              {/* Version selection */}
              <div className="space-y-2 p-4 bg-card border border-border rounded-md">
                <h4 className="text-sm font-medium text-foreground mb-2">Wybierz wersję do zapisu:</h4>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="version"
                      value="original"
                      checked={selectedVersion === 'original'}
                      onChange={() => setSelectedVersion('original')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">Zapisz oryginalny tekst</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="version"
                      value="processed"
                      checked={selectedVersion === 'processed'}
                      onChange={() => setSelectedVersion('processed')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">
                      Zapisz przetworzony tekst {editableProcessedText !== processedText && '(edytowany)'}
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={isProcessing}>
                  {isProcessing ? 'Zapisywanie...' : 'Zapisz wybraną wersję'}
                </Button>
                <Button onClick={() => setIsProcessed(false)} variant="outline" disabled={isProcessing}>
                  Wróć do edycji
                </Button>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {/* Saved state */}
          <div className="p-6 bg-card border border-border rounded-md text-center space-y-4">
            <div className="text-green-600 text-5xl">✓</div>
            <h4 className="text-lg font-medium text-foreground">Notatka zapisana pomyślnie!</h4>
            <p className="text-sm text-muted-foreground">
              Twoja notatka została dodana do spotkania.
            </p>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleNewNote}>Dodaj kolejną notatkę</Button>
          </div>
        </>
      )}
    </div>
  );
};
